import React from "react";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import { Image } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  List,
  Card,
  Thumb,
  Body,
  Title,
  Meta,
  Buttons,
  EditButton,
  DeleteButton,
} from "./index.style";

export type TrackStatus = "Draft" | "Publish" | "publicado" | "pendente" | "rascunho";

export interface Track {
  id: string | number;
  title: string;
  lessons?: number;
  numberOfLessons?: number;
  status: TrackStatus;
  imageUrl?: string | null;
  length?: number;
  totalOfMinutes?: number;
  description?: string;
  createdAt?: string;
}

export type InfoItem = {
  icon: React.ReactNode;
  text: string;
};

export type TrackAction = {
  label: string;
  kind?: "edit" | "delete" | "primary" | "outline";
  onClick?: (id: Track["id"]) => void;
};

interface TrackContainerProps {
  tracks: Track[];
  getInfoItems: (track: Track) => InfoItem[];
  getActions: (track: Track) => TrackAction[];
  onCardClick?: (track: Track) => void;
}

export const TrackContainer: React.FC<TrackContainerProps> = ({
  tracks,
  getInfoItems,
  getActions,
  onCardClick,
}) => {
  return (
    <List>
      {tracks.map((t) => (
        <TrackCard
          key={t.id}
          track={t}
          info={getInfoItems(t).slice(0, 2)}
          actions={getActions(t).slice(0, 2)}
          onCardClick={onCardClick}
        />
      ))}
    </List>
  );
};

const TrackCard: React.FC<{
  track: Track;
  info: InfoItem[];
  actions: TrackAction[];
  onCardClick?: (track: Track) => void;
}> = ({ track, info, actions, onCardClick }) => {
  const navigate = useNavigate();
  const open = (): void => {
    if (onCardClick) {
      onCardClick(track);
    } else {
      navigate(`/hub/available-tracks/details/${track.id}`, { state: { track } });
    }
  };
  return (
    <Card onClick={open}>
      <Thumb>
        {track.imageUrl ? (
          <Image 
            src={track.imageUrl} 
            alt={track.title}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallbackElement = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallbackElement) {
                fallbackElement.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div 
          className="w-100 h-100 d-flex align-items-center justify-content-center text-secondary"
          style={{ display: track.imageUrl ? 'none' : 'flex' }}
        >
        </div>
      </Thumb>

      <Body>
        <Title>{track.title}</Title>

        {info.length > 0 && (
          <Meta>
            {info.map((it, idx) => (
              <span
                key={idx}
                className="d-inline-flex align-items-center gap-2"
              >
                {it.icon}
                <span>{it.text}</span>
              </span>
            ))}
          </Meta>
        )}

        {actions.length > 0 && (
          <Buttons data-count={actions.length}>
            {actions.map((a, idx) => {
              const click = () => a.onClick?.(track.id);

              if (a.kind === "delete") {
                return (
                  <DeleteButton
                    key={idx}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      click();
                    }}
                    disabled={!a.onClick}
                  >
                    {a.label}
                  </DeleteButton>
                );
              }

              // "edit", "primary" e "outline" usam EditButton (visual laranja)
              return (
                <EditButton
                  key={idx}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    click();
                  }}
                  disabled={!a.onClick}
                >
                  {a.label}
                </EditButton>
              );
            })}
          </Buttons>
        )}
      </Body>
    </Card>
  );
};
